// SMSDashboardHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SmsIcon from "@mui/icons-material/Sms";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartIcon from "@mui/icons-material/TableChart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };

const DEFAULT_PDFS = [
  {
    title: "SMS Dashboard Help Manual",
    href: "https://mdimages-test.s3.amazonaws.com/baa8e30e-2dd4-4ffc-934d-d66cad741d7d_SMS_Dashboard_Help_Manual.pdf",
  },
];

// ---- Static Content ----

const PIPELINE_STEPS: StepBlock[] = [
  { title: "Action Taken in Zoho DMS", bullets: ["A dealer performs a servicing-related action (e.g., booking confirmed, service completed)."] },
  { title: "Zoho Sends a Payload", bullets: ["Zoho DMS sends Machadalo a payload containing the message to be sent."] },
  { title: "Machadalo Applies Processing Logic", bullets: ["Removes duplicate messages within 48 hours.", "Applies template deactivation logic."] },
  { title: "Forwarded to TrustSignal (Sigmo)", bullets: ["The processed payload is forwarded to Machadalo's messaging partner."] },
  { title: "Sent via Vodafone to Customer", bullets: ["TrustSignal forwards the payload to Vodafone, which delivers the SMS."] },
];

const EXCEL_SHEETS = [
  { name: "Data", desc: "Raw data of API hits — paste downloaded SMS logs here (up to column O only)." },
  { name: "Summary", desc: "DLT templates with Count, Unique Count, and Delivery rates." },
  { name: "Duplicate List", desc: "All messages with how many times each was sent." },
  { name: "Distribution", desc: "Message volume grouped by duplication frequency." },
  { name: "Reg No", desc: "Vehicle registration numbers with invalid character length (under 8 or over 15)." },
  { name: "Phone Number", desc: "Invalid phone number formats found in the logs." },
];

const ANALYZE_STEPS: StepBlock[] = [
  { title: "Download Last 7 Days of Logs", bullets: ["From the SMS Dashboard, select the last 7 days and download the report."] },
  { title: "Open the Excel Analysis Sheet", bullets: ["Open the pre-built Excel Analysis Sheet on your computer."] },
  { title: "Paste Data", bullets: ["In the Data sheet, paste the downloaded log data up to column O only.", "Columns P-Z contain formulas that apply automatically."] },
  { title: "Refresh Pivot Table", bullets: ["Go to Duplicate Count sheet → Pivot Table Analyze → Refresh All."] },
  { title: "Review Report", bullets: ["The analytical report across all sheets is now updated."] },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const SMSDashboardHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");

    return {
      overview: "sms dashboard mercedes-benz zoho dms dealer management system logs vodafone",
      pipeline: flatten(PIPELINE_STEPS) + " zoho machadalo trustsigmo sigmo vodafone",
      access: "sms dashboard date range download report",
      excelAnalysis: flatten(ANALYZE_STEPS) + " excel analysis sheet duplicate count pivot table refresh",
      excelSheets: "data summary duplicate list distribution reg no phone number",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="SMS Dashboard Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SmsIcon />
              <Typography variant="h6" fontWeight={800}>
                SMS Dashboard • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search pipeline, excel, duplicates, DLT, templates…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Pipeline" size="small" variant="outlined" onClick={() => jumpTo("#sec-pipeline")} sx={{ mb: 1 }} />
              <Chip label="Access" size="small" variant="outlined" onClick={() => jumpTo("#sec-access")} sx={{ mb: 1 }} />
              <Chip label="Excel Analysis" size="small" variant="outlined" onClick={() => jumpTo("#sec-excel")} sx={{ mb: 1 }} />
              <Chip label="Excel Sheets" size="small" variant="outlined" onClick={() => jumpTo("#sec-excel-sheets")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Complete logs of every SMS sent to Mercedes-Benz customers." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    The SMS Dashboard contains the complete logs of every SMS sent to Mercedes-Benz customers as part of their vehicle servicing journey.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>Client</Typography>
                        <Typography variant="body2">Mercedes-Benz</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>Source</Typography>
                        <Typography variant="body2">Zoho DMS</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={4}>
                      <Card variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography variant="body2" fontWeight={600}>Partner</Typography>
                        <Typography variant="body2">TrustSignal (Sigmo)</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Card>
              </>
            )}

            {/* Pipeline */}
            {matches("pipeline") && (
              <>
                <SectionHeader id="sec-pipeline" title="SMS Delivery Pipeline" />
                <StepAccordion steps={PIPELINE_STEPS} label="Pipeline Steps" defaultExpanded />
                <Alert severity="info" sx={{ mt: 1 }}>
                  <strong>Deduplication:</strong> Removes duplicate messages within a 48-hour window to prevent customers from receiving the same servicing notification multiple times.
                </Alert>
              </>
            )}

            {/* Access */}
            {matches("access") && (
              <>
                <SectionHeader id="sec-access" title="Accessing the SMS Dashboard" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="1. Open the SMS Dashboard on the Machadalo platform." />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="2. Select a Date Range for the logs you want to view." />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="3. Click Download to export the SMS logs." />
                    </ListItem>
                  </List>
                </Card>
              </>
            )}

            {/* Excel Analysis */}
            {matches("excelAnalysis") && (
              <>
                <SectionHeader id="sec-excel" title="Excel Analysis Sheet" />
                <StepAccordion steps={ANALYZE_STEPS} label="Analysis Steps" defaultExpanded />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>Important:</strong> Always paste new data only up to column O. Do not overwrite or alter the formulas in columns P-Z, as this will break the automatic analysis.
                </Alert>
              </>
            )}

            {/* Excel Sheets */}
            {matches("excelSheets") && (
              <>
                <SectionHeader id="sec-excel-sheets" title="Excel Sheet Reference" />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Sheet</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {EXCEL_SHEETS.map((sheet) => (
                        <TableRow key={sheet.name}>
                          <TableCell>{sheet.name}</TableCell>
                          <TableCell>{sheet.desc}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <strong>Reg No and Phone Number sheets:</strong> Used for data-quality checks — surface likely bad data coming through from the DMS payloads.
                </Alert>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default SMSDashboardHelp;